import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Accordion from 'LuxKit/Accordion'
import Divider from 'LuxKit/Divider'
import LineCardAtmIcon from 'LuxKit/Icons/line/LineCardAtmIcon'
import LineFileInfoAltIcon from 'LuxKit/Icons/line/LineFileInfoAltIcon'
import LineHomeAltIcon from 'LuxKit/Icons/line/LineHomeAltIcon'
import LineKeySkeletonAltIcon from 'LuxKit/Icons/line/LineKeySkeletonAltIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE } from 'constants/dateFormats'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { format24HourTime } from 'lib/datetime/timeUtils'
import { scrollToHash } from 'lib/window/scrollUtils'
import { rem } from 'polished'
import React, { useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router'
import styled from 'styled-components'

const CheckInDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr 0px;
  grid-gap: ${rem(24)};
  overflow: hidden;

  >:last-child {
    grid-column: span 4;
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: max-content 1px max-content 1px 1fr;

    >:last-child {
      grid-column: unset;
    }
  }
`

interface Props {
  offer: App.VillaOffer
}

function VillasThingsToKnow(props: Props) {
  const { offer } = props
  const intl = useIntl()
  const { heights } = useContext(HeaderHeightContext)

  const location = useLocation()

  useEffect(() => {
    scrollToHash(location.hash, true)
  }, [location])

  return (
    <Group direction="vertical" gap={32}>
      <Heading variant="heading2">
        {intl.formatMessage({
          id: '6t8+R+xToE',
          defaultMessage: 'Things to know',
          description: 'Section title for villa property details and house rules',
        })}
      </Heading>
      <CheckInDetails>
        <VerticalSpacer gap={4}>
          <Caption variant="large" colour="neutral-three">
            {intl.formatMessage({
              id: 'z8MVA895se',
              defaultMessage: 'Check-in',
              description: 'Label for villa property check-in time',
            })}
          </Caption>
          <BodyText variant="large" weight="bold">
            {!offer.property.checkInTo && offer.property.checkInFrom && intl.formatMessage(
              {
                id: 'Z8op5x6lFx',
                defaultMessage: 'After {time}',
                description: 'Check-in time when only start time is specified for villa property',
              },
              { time: format24HourTime(offer.property.checkInFrom, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE) },
            )}
            {!!offer.property.checkInTo && offer.property.checkInFrom && intl.formatMessage(
              {
                id: 'OWb+sY/Kmi',
                defaultMessage: 'Between {startTime} and {endTime}',
                description: 'Check-in time range for villa property',
              },
              {
                startTime: format24HourTime(offer.property.checkInFrom, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE),
                endTime: format24HourTime(offer.property.checkInTo, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE),
              },
            )}
          </BodyText>
        </VerticalSpacer>
        <Divider direction="vertical" kind="primary" />
        <VerticalSpacer gap={4}>
          <Caption variant="large" colour="neutral-three">
            {intl.formatMessage({
              id: '1d3qUYbWwK',
              defaultMessage: 'Check-out',
              description: 'Label for villa property check-out time',
            })}
          </Caption>
          {offer.property.checkOutUntil && <BodyText variant="large" weight="bold">
            {intl.formatMessage(
              {
                id: 'bhn4GyEvmV',
                defaultMessage: 'Before {time}',
                description: 'Check-out time for villa property',
              },
              { time: format24HourTime(offer.property.checkOutUntil, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE) },
            )}
          </BodyText>}
        </VerticalSpacer>
        <Divider direction="vertical" kind="primary" />
        <VerticalSpacer gap={4}>
          <BodyTextBlock variant="medium" weight="bold" startIcon={<LineKeySkeletonAltIcon />}>
            {intl.formatMessage({
              id: 'GvMR/R2E9i',
              defaultMessage: 'Meet & greet check-in',
              description: 'Label for villa meet and greet check-in service',
            })}
          </BodyTextBlock>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'oDeKmu9YVG',
              defaultMessage: 'At check-in, you’ll be greeted by the staff, ensuring a stress-free arrival.',
              description: 'Description of villa meet and greet check-in service',
            })}
          </BodyText>
        </VerticalSpacer>
      </CheckInDetails>
      <VerticalSpacer gap={20}>
        <Heading variant="heading5">
          {intl.formatMessage({
            id: 'sEX00N6HVb',
            defaultMessage: 'Important Info',
            description: 'Subsection title for important villa property information',
          })}
        </Heading>
        <Group direction="vertical" noDisplayWhenEmpty>
          { offer.property.copy.houseRules && <Accordion
            startIcon={<LineHomeAltIcon />}
            heading={intl.formatMessage({
              id: 'MG7gAWv7iT',
              defaultMessage: 'House rules',
              description: 'Accordion heading for villa house rules section',
            })}
            scrollOnOpen
            scrollMarginTop={heights.siteStickyBanner}
          >
            <MarkdownRender content={offer.property.copy.houseRules} fontSize="medium" />
          </Accordion>}
          {offer.property.copy.paymentDetails && <Accordion
            startIcon={<LineCardAtmIcon />}
            heading={intl.formatMessage({
              id: '66rrV9hcob',
              defaultMessage: 'Security Deposit',
              description: 'Accordion heading for villa security deposit section',
            })}
            scrollOnOpen
            scrollMarginTop={heights.siteStickyBanner}
          >
            <MarkdownRender content={offer.property.copy.paymentDetails} fontSize="medium" />
          </Accordion>}
          {offer.property.copy.finePrint && <Accordion
            startIcon={<LineFileInfoAltIcon />}
            id="fine-print"
            heading={intl.formatMessage({
              id: 'izjLeXdzqv',
              defaultMessage: 'Fine print',
              description: 'Accordion heading for villa fine print section',
            })}
            scrollMarginTop={heights.siteStickyBanner}
            scrollOnOpen
            openOnHash
          >
            <MarkdownRender content={offer.property.copy.finePrint} fontSize="medium" />
          </Accordion>}
        </Group>
      </VerticalSpacer>
    </Group>
  )
}

export default VillasThingsToKnow
